import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import {
  contentAnalyticsResponseSchema,
  type ContentAnalyticsResponse,
} from "@community-analytics/shared";

import { apiFetch } from "@shared/api";

import { messageQueryKeys } from "./keys";

/**
 * GET /api/communities/:id/content — TASK-034 wire contract.
 *
 * The hook is a no-op when `communityId` is null (e.g. before the
 * communities list resolves and the active-community store picks one) —
 * `enabled: false` keeps TanStack Query from firing a request with a
 * malformed URL.
 *
 * Backend returns the full payload in one shot (topPosts, activityHeatmap,
 * optimalSlots) per §6.3.3. The window is fixed at 28 days (declared in
 * shared/content.ts as a literal type), so no query parameters are needed
 * in the MVP — the date-range picker store from features/filter-date-range
 * is wired through but currently has no effect on the request URL. Once
 * the backend accepts a `windowDays` param the hook will start passing it.
 */
export function useContentQuery(
  communityId: string | null,
): UseQueryResult<ContentAnalyticsResponse, Error> {
  return useQuery<ContentAnalyticsResponse, Error>({
    queryKey: messageQueryKeys.content(communityId ?? "__noop__"),
    enabled: communityId !== null,
    queryFn: async () => {
      if (communityId === null) throw new Error("communityId is required");
      const raw = await apiFetch<unknown>(
        `/api/communities/${encodeURIComponent(communityId)}/content`,
      );
      return contentAnalyticsResponseSchema.parse(raw);
    },
  });
}
